import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { defaultHomeContent, localized } from '../lib/homeContent';
import { useLanguage } from '../context/LanguageContext';
import { resolvePublicAssetUrl } from '../lib/assetUrl';
import { getCachedHomeContentSync, loadHomeContent } from '../lib/homeContentLoader';
import { isSpanishOnlyPath } from '../lib/localeRoutes';

function isExternalLink(href = '') {
  return /^https?:\/\//i.test(href);
}

const SOCIAL_ICONS = {
  Instagram: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="5" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  Facebook: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="black">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  TikTok: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="black">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 10.86 4.48V13.2a8.28 8.28 0 0 0 5.58 2.15V12a4.83 4.83 0 0 1-5.58-2.15V2h3.45v4.69z" />
    </svg>
  ),
  Twitter: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="black">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
};

function Footer({ content = defaultHomeContent.footer, logoClassName = 'mb-3 w-full max-w-[280px] object-contain object-left md:mb-4 md:max-w-[340px]' }) {
  const [resolvedContent, setResolvedContent] = useState(() => (content === defaultHomeContent.footer ? getCachedHomeContentSync().footer : content));
  const footer = resolvedContent || defaultHomeContent.footer;
  const { language, t } = useLanguage();
  const logoUrl = resolvePublicAssetUrl(footer.logoUrl || defaultHomeContent.footer.logoUrl || '/images/footer-logo-big.png');
  const footerBackgroundColor = footer.backgroundColor || defaultHomeContent.footer.backgroundColor || '#FAFAFA';

  useEffect(() => {
    if (content !== defaultHomeContent.footer) {
      setResolvedContent(content || defaultHomeContent.footer);
      return;
    }

    let alive = true;
    loadHomeContent()
      .then((homeContent) => {
        if (alive) setResolvedContent(homeContent.footer || defaultHomeContent.footer);
      })
      .catch(() => {
        if (alive) setResolvedContent(defaultHomeContent.footer);
      });

    return () => {
      alive = false;
    };
  }, [content]);

  return (
    <footer style={{ backgroundColor: footerBackgroundColor }}>
      <div className="mx-auto max-w-[1345px] px-4 pt-14 pb-6 md:px-8 md:pt-16 md:pb-7">
        <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr] md:gap-12 md:items-start">
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <img src={logoUrl} alt="MiskiPOP" width={600} height={196} loading="lazy" decoding="async" className={logoClassName} />
            <p className="max-w-[416px] text-center text-[16px] font-medium leading-[24px] text-[#555555] md:text-left">
              {localized(footer.description, language, 'Tienda oficial con artículos únicos y personalizados para que cada fan pueda sentirse parte de algo más grande.')}
            </p>
          </div>

          <div className="col-span-1 flex flex-col items-center sm:items-start">
            <h3 className="mb-5 text-[18px] sm:text-[20px] font-bold text-black text-center sm:text-left">{localized(footer.linksTitle, language, 'EXPLORAR')}</h3>
            <ul className="flex flex-col gap-2.5 text-center sm:text-left">
              {(footer.links || []).slice(0, 5).map((link, index) => {
                const label = localized(link.label, language, '');
                const rawHref = link.href;
                const safeHref = typeof rawHref === 'string' ? rawHref : (rawHref?.es || rawHref?.en || '');
                return (
                <li key={`${label}-${safeHref || index}`}>
                  {(label.toLowerCase().includes('contacto') || label.toLowerCase().includes('soporte')) ? (
                    <Link to="/contacto" className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </Link>
                  ) : !safeHref || safeHref === '#' ? (
                    <span className="text-[14px] font-medium text-[#555555]">
                      {label}
                    </span>
                  ) : isExternalLink(safeHref) ? (
                    <a href={safeHref} target="_blank" rel="noopener noreferrer" className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </a>
                  ) : isSpanishOnlyPath(safeHref) ? (
                    <a href={safeHref} className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">{label}</a>
                  ) : (
                    <Link to={safeHref === '/' ? '/' : safeHref.replace(/\/$/, '')} className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </Link>
                  )}
                </li>
                );
              })}
            </ul>
          </div>

          <div className="col-span-1 flex flex-col items-center sm:items-start">
            <h3 className="mb-5 text-[18px] sm:text-[20px] font-bold text-black text-center sm:text-left">{localized(footer.helpLinksTitle, language, 'AYUDA')}</h3>
            <ul className="flex flex-col gap-2.5 text-center sm:text-left">
              {(footer.helpLinks || []).map((link, index) => {
                const label = localized(link.label, language, '');
                const rawHref = link.href;
                const safeHref = typeof rawHref === 'string' ? rawHref : (rawHref?.es || rawHref?.en || '');
                return (
                <li key={`${label}-${safeHref || index}`}>
                  {(label.toLowerCase().includes('contacto') || label.toLowerCase().includes('soporte')) ? (
                    <Link to="/contacto" className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </Link>
                  ) : !safeHref || safeHref === '#' ? (
                    <span className="text-[14px] font-medium text-[#555555]">
                      {label}
                    </span>
                  ) : isExternalLink(safeHref) ? (
                    <a href={safeHref} target="_blank" rel="noopener noreferrer" className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </a>
                  ) : isSpanishOnlyPath(safeHref) ? (
                    <a href={safeHref} className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">{label}</a>
                  ) : (
                    <Link to={safeHref === '/' ? '/' : safeHref.replace(/\/$/, '')} className="text-[14px] font-medium text-[#555555] transition-colors hover:text-[#FF50A3]">
                      {label}
                    </Link>
                  )}
                </li>
                );
              })}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <h3 className="mb-5 text-[20px] font-bold text-black">{t('footer.followUs')}</h3>
            <p className="mb-5 text-center text-[14px] font-medium leading-[22px] text-[#555555] md:text-left">
              {localized(footer.socialDescription, language, 'Entérate de lanzamientos y promociones exclusivas')}
            </p>
            <div className="flex items-center gap-4">
              {(footer.socials || []).map((social) => (
                <a key={typeof social.label === 'string' ? social.label : JSON.stringify(social.label)} href={social.href} target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70" aria-label={localized(social.label, language, '') || 'Social'}>
                  {SOCIAL_ICONS[localized(social.label, language, '')] || (social.iconUrl ? (
                    <img src={resolvePublicAssetUrl(social.iconUrl)} alt={localized(social.label, language, '') || 'Social'} width={26} height={26} loading="lazy" decoding="async" className="h-[26px] w-[26px] object-contain" />
                  ) : null) || (
                    <span className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                      {(localized(social.label, language, '') || 'S').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        <p className="pt-5 text-center text-[13px] text-[#666666] md:pt-6" suppressHydrationWarning>
          &copy; {new Date().getFullYear()} MiskiPOP, {language === 'en' ? 'All rights reserved' : 'Todos los derechos reservados'}
        </p>
      </div>
    </footer>
  );
}

export default Footer;
